import React from 'react'
import { useTemplateResetPassword } from './useTemplateResetPassword'
import { useTemplateConfirmDialog } from './useTemplateConfirmDialog'
import type { PromptDialogOptions, ConfirmDialogOptions } from '@ztechapps/common.hook'
import { useConfirmDialog, useError } from '@ztechapps/common.hook'
import { useCommunicationTemplateVariableReplace, usePromptDialog } from '@ztechapps/common.hook'
import { jsonTryParse } from '@ztechapps/common.utils'
import { PromptDialog } from './promptDialog'
import type { TemplateVariables } from '../Context'

const ConfirmationTypeEnum = {
  useConfirmDialog: 'useConfirmDialog',
  usePromptDialog: 'usePromptDialog',
}

export type UseTemplateVariables = [
  (template: string) => Promise<void>,
  { variables: TemplateVariables; generatedValue: string },
]

export const useTemplateVariables = (
  templateVariables: TemplateVariables,
  templateVariableConfirmDialogConfigs?: Record<any, Partial<ConfirmDialogOptions>>
): UseTemplateVariables => {
  const { getConfirmConfig } = useTemplateConfirmDialog(templateVariableConfirmDialogConfigs)
  const { onError } = useError()
  const { resetPassword: resetPasswordMain } = useTemplateResetPassword()
  const [generatedValue, setGeneratedValue] = React.useState<string>()

  const defaultDialogConfig: PromptDialogOptions | ConfirmDialogOptions = {
    text: `Are you sure you want to apply this template? Some templates are non revertible. It may take effect instantly once you click the apply button.`,
    title: `Apply template`,
    buttons: { confirm: { text: `Yes, apply it!` } },
  }

  const { confirm } = useConfirmDialog(defaultDialogConfig)

  const { prompt, setPromptAction } = usePromptDialog(defaultDialogConfig)

  const getValues = (template: string, confirmed: (template: string, variables: TemplateVariables) => any) => {
    const valueFromVariable = /{{(.*?)}}/g.exec(template)

    //split the value
    const valueArr = valueFromVariable?.[1]?.split(' ') ?? []

    //check if the first item in valueArr is useConfirmDialog
    const isConfirmDialog = valueArr?.[0] === ConfirmationTypeEnum.useConfirmDialog

    //check if the first item in valueArr is usePromptDialog
    const isPromptDialog = valueArr?.[0] === ConfirmationTypeEnum.usePromptDialog

    //if usePromptDialog then show the prompt dialog
    if (isPromptDialog) {
      const promptDialogOptions: PromptDialogOptions = {
        component: (
          <PromptDialog
            variableName={valueArr?.[1]}
            setPromptAction={setPromptAction}
            templateVariables={templateVariables}
          />
        ),
      }

      return prompt(
        (value: Record<any, any>) => {
          //swal value is always a string, so we need to parse it
          value = jsonTryParse(value)
          //replace or remove the string with {{usePromptDialog}}
          template = template.replace(valueFromVariable?.[0], '')

          //this will be the custom variables that will be applied when usePromptDialog
          const newVariables: TemplateVariables = {
            ...templateVariables,
            resetPassword: () => {
              return resetPasswordMain({ _id: value?.resetPasswordId })
            },
            username: value?.username,
            profileName: value?.profileName,
          }

          confirmed?.(template, newVariables)
        },
        undefined,
        promptDialogOptions
      )
    }
    //if useConfirmDialog then show the confirm dialog
    else if (isConfirmDialog) {
      return confirm(
        async () => {
          //replace or remove the string with {{useConfirmDialog}}
          template = template.replace(valueFromVariable?.[0], '')
          confirmed?.(template, templateVariables)
        },
        undefined,
        getConfirmConfig(valueArr?.[1])
      )
    } else {
      confirmed?.(template, templateVariables)
    }
  }

  const templateVariableReplace = useCommunicationTemplateVariableReplace()
  const generate = async (template: string) => {
    if (template) {
      const confirmed = async (value: string, newVariables: TemplateVariables) => {
        try {
          newVariables = newVariables ?? templateVariables
          const renderValue = await templateVariableReplace.execute(value, newVariables)
          setGeneratedValue(renderValue)
        } catch (e) {
          onError(e)
        }
      }

      await getValues(template, confirmed)
    } else {
      setGeneratedValue(null)
    }
  }

  return [generate, { variables: templateVariables, generatedValue }]
}
